import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { refreshSession } from "./lib/api-client";
import { startSessionKeepalive } from "./lib/session-keepalive";

/**
 * App shell shared by BOTH entries. The router provider is passed as
 * `children` — a `<RouterProvider>` from `main.tsx` in the browser, a
 * `<StaticRouterProvider>` from `entry-server.tsx` during prerendering — and
 * the QueryClient is owned by the entry so the prerenderer can seed one per
 * route and the browser can hydrate from the embedded state.
 */
export function App({ queryClient, children }: { queryClient: QueryClient; children: ReactNode }) {
  // Keep the short-lived access cookie fresh on the gated dev/staging hosts by
  // proactively refreshing it before it expires (the edge gate can't refresh, so
  // a silent expiry would otherwise bounce an active user to login mid-session).
  // Prod web is OPEN (no session gate), so we must NOT pester /refresh there.
  // Effects never run during prerendering, so this is server-safe as-is.
  useEffect(() => {
    if (import.meta.env.VITE_ENV === "production") return;
    return startSessionKeepalive(refreshSession);
  }, []);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
